// @ts-expect-error
import { createContext, useContext } from "react";
// This is not at parity with the backend PartnerConfiguration class
// See Common/partnerContex/* for configurations of partnerships
export type PartnerContextValues = {
  partnerContextEnabled: boolean;
  partner: string;
  partnerName: string;
  partnerCasualName: string;
  partnerManagedW2s: boolean;
  partnerManaged1099s: boolean;
  partnerManagedOwners: boolean;
  siManagedW2s: boolean;
  siManaged1099s: boolean;
  siManagedOwners: boolean;
  partnerHasWageReport: boolean;
  reviewOnlyFields: {
    companyName?: true;
    organizationType?: true;
    contactName?: true;
    contactTitle?: true;
    fein?: true;
    contactPhoneNumber?: true;
    mailingAddress?: true;
  };
  syncedEmployeeFields: {
    bornAt?: true;
    employeeType?: true;
    fullName?: true;
    gender?: true;
    hiredAt?: true;
    homePhoneNumber?: true;
    maritalStatus?: true;
    jobTitle?: true;
    physicalAddress?: true;
    socialSecurityNumber?: true;
    workPhoneNumber?: true;
  };
  includeAccountCreationStep: boolean;
  color?: string;
  partnerLogo?: string;
  supportEmail: string;
  supportPhone: string;
  routerPrefix?: boolean;
};
export const defaultContextValues: PartnerContextValues = {
  partnerContextEnabled: false,
  partner: "none",
  partnerName: "none",
  partnerCasualName: "none",
  partnerManagedW2s: true,
  partnerManaged1099s: false,
  partnerManagedOwners: false,
  siManagedW2s: false,
  siManaged1099s: true,
  siManagedOwners: true,
  partnerHasWageReport: false,
  reviewOnlyFields: {},
  syncedEmployeeFields: {},
  includeAccountCreationStep: true,
  supportEmail: "support@simplyinsured.com",
  supportPhone: "(888) 584-7457",
};
// Neat Typescript feature: we can generate TypeGuard and Validate methods from type interfaces to use at runtime, useful for validating ajax response payloads
// https://javascript.plainenglish.io/how-to-automatically-convert-typescript-types-to-runtime-validators-5b06ee269b13
export const validateContextValues = (values: PartnerContextValues) => {
  let hasErrors = false;

  // Start validations
  if (typeof values.partnerContextEnabled !== "boolean") {
    console.error("values.partnerContextEnabled should be a boolean");
    hasErrors = true;
  }

  if (typeof values.partnerManagedW2s !== "boolean") {
    console.error("values.partnerManagedW2s should be a boolean");
    hasErrors = true;
  }

  if (typeof values.partnerManaged1099s !== "boolean") {
    console.error("values.partnerManaged1099s should be a boolean");
    hasErrors = true;
  }

  if (typeof values.partnerManagedOwners !== "boolean") {
    console.error("values.partnerManagedOwners should be a boolean");
    hasErrors = true;
  }

  if (typeof values.siManagedW2s !== "boolean") {
    console.error("values.siManagedW2s should be a boolean");
    hasErrors = true;
  }

  if (typeof values.siManaged1099s !== "boolean") {
    console.error("values.siManaged1099s should be a boolean");
    hasErrors = true;
  }

  if (typeof values.siManagedOwners !== "boolean") {
    console.error("values.siManagedOwners should be a boolean");
    hasErrors = true;
  }

  if (typeof values.partnerHasWageReport !== "boolean") {
    console.error("values.partnerHasWageReport should be a boolean");
    hasErrors = true;
  }

  if (typeof values.partner !== "string") {
    console.error("values.partner should be a string.");
    hasErrors = true;
  }

  if (typeof values.partnerName !== "string") {
    console.error("values.partnerName should be a string.");
    hasErrors = true;
  }

  if (typeof values.reviewOnlyFields !== "object") {
    console.error("values.reviewOnlyFields should be a POJO object.");
    hasErrors = true;
  }

  if (typeof values.syncedEmployeeFields !== "object") {
    console.error("values.syncedEmployeeFields should be a POJO object.");
    hasErrors = true;
  }

  if (typeof values.includeAccountCreationStep !== "boolean") {
    console.error("values.includeAccountCreationStep should be a boolean");
    hasErrors = true;
  }

  if (typeof values.supportEmail !== "string") {
    console.error("values.supportEmail should be a string.");
    hasErrors = true;
  }

  if (typeof values.supportPhone !== "string") {
    console.error("values.supportPhone should be a string.");
    hasErrors = true;
  }

  // End validations
  if (hasErrors) {
    throw new Error("Context has errors. Check your console for error messages.");
  } else {
    return values;
  }
};
const PartnerContext = createContext(validateContextValues(defaultContextValues));
// Favor hook when using inside a Functional Component.

/**
 * const MyComponent = () => {
 *   const { AddEmployeesIntro } = usePartnerContext();
 *   ...
 *   return (
 *     <div>
 *       ...
 *       <AddEmployeesIntro />
 *       ...
 *     </div>
 *   )
 * }
 */
export const usePartnerContext = () => useContext(PartnerContext);
export default PartnerContext;
